import React, { useState, useEffect } from 'react';
import { MapPinIcon, SignalIcon, CheckCircleIcon, ExclamationTriangleIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import { useDispatcherLocation } from '../../hooks/useDispatcherLocation';

interface LocationTrackerProps {
  autoStart?: boolean;
}

const LocationTracker: React.FC<LocationTrackerProps> = ({
  autoStart = true,
}) => {
  const {
    location,
    isAvailable,
    isTracking,
    lastUpdate,
    error,
    permission,
    startTracking,
    stopTracking,
    toggleAvailability,
  } = useDispatcherLocation();

  const [success, setSuccess] = useState<string | null>(null);

  // Show success message when availability changes
  useEffect(() => {
    if (isAvailable && location) {
      setSuccess(`You are now ${isAvailable ? 'available' : 'unavailable'} for orders`);
      const timer = setTimeout(() => setSuccess(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [isAvailable]);

  // Auto-start tracking if enabled
  useEffect(() => {
    if (autoStart && permission === 'granted' && !isTracking) {
      startTracking();
    }
  }, [autoStart, permission, isTracking, startTracking]);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
          <MapPinIcon className="w-6 h-6 text-sky-600 mr-2" />
          Real-Time Location Tracking
        </h3>
        <div className="flex items-center space-x-2">
          <div className={`w-2 h-2 rounded-full ${isTracking ? 'bg-green-500 animate-pulse' : 'bg-gray-300'}`}></div>
          <span className="text-sm text-gray-600">
            {isTracking ? 'Tracking' : 'Inactive'}
          </span>
        </div>
      </div>

      {/* Location Permission Warning */}
      {permission === 'denied' && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-2">
          <ExclamationTriangleIcon className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-red-800">
            <p className="font-medium">Location access denied</p>
            <p className="text-xs mt-1">Please enable location services in your browser settings to use this feature.</p>
          </div>
        </div>
      )}

      {/* iOS Background Tracking Notice */}
      {isTracking && /iPhone|iPad|iPod/.test(navigator.userAgent) && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-start space-x-2">
          <LockClosedIcon className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium">Keep this page open</p>
            <p className="text-xs mt-1">For best tracking on iOS, keep this page open and your screen unlocked while delivering.</p>
          </div>
        </div>
      )}

      {/* Current Location Display */}
      {location && (
        <div className="mb-4 p-4 bg-sky-50 rounded-lg">
          <div className="text-sm text-gray-700">
            <p className="font-medium mb-2">Current Coordinates:</p>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div>
                <span className="text-gray-600">Latitude:</span>
                <span className="ml-2 font-mono">{location.lat.toFixed(6)}</span>
              </div>
              <div>
                <span className="text-gray-600">Longitude:</span>
                <span className="ml-2 font-mono">{location.lng.toFixed(6)}</span>
              </div>
            </div>
            {lastUpdate && (
              <p className="mt-2 text-xs text-gray-500">
                Last updated: {lastUpdate.toLocaleTimeString()}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Availability Toggle */}
      <div className="mb-4 flex items-center justify-between p-3 bg-gray-50 rounded-lg">
        <div className="flex items-center space-x-2">
          <SignalIcon className={`w-5 h-5 ${isAvailable ? 'text-green-600' : 'text-gray-400'}`} />
          <span className="font-medium text-gray-700">
            {isAvailable ? 'Available for orders' : 'Unavailable'}
          </span>
        </div>
        <button
          onClick={toggleAvailability}
          disabled={!location}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
            isAvailable ? 'bg-green-600' : 'bg-gray-300'
          } ${!location ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              isAvailable ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      </div>

      {/* Automatic Tracking Status */}
      {isTracking && (
        <div className="mb-4 p-4 bg-gradient-to-r from-green-50 to-sky-50 border border-green-200 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="relative">
                <MapPinIcon className="w-5 h-5 text-green-600" />
                <div className="absolute -top-1 -right-1 w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-800">Automatic Tracking Active</p>
                <p className="text-xs text-gray-600">Location updates every 7 seconds in the background</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Waiting for Permission */}
      {!isTracking && permission === 'prompt' && (
        <div className="mb-4 p-4 bg-sky-50 border border-sky-200 rounded-lg">
          <div className="flex items-center space-x-2">
            <MapPinIcon className="w-5 h-5 text-sky-600" />
            <div>
              <p className="text-sm font-medium text-gray-800">Enable Location Access</p>
              <p className="text-xs text-gray-600">Grant permission to start automatic tracking</p>
            </div>
          </div>
        </div>
      )}

      {/* Success Message */}
      {success && (
        <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center space-x-2">
          <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0" />
          <p className="text-sm text-green-800">{success}</p>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {/* Privacy Notice */}
      <div className="mt-4 p-3 bg-gray-50 rounded-lg">
        <p className="text-xs text-gray-600">
          <span className="font-medium">Privacy:</span> Your location is only shared with customers when you accept a delivery and is used to calculate accurate ETAs.
        </p>
      </div>
    </div>
  );
};

export default LocationTracker;
